import { FORBIDDEN_PATH, LOGOUT_PATH, UNAUTHORIZED_PATH } from '../constants';
import Cookies from 'universal-cookie';
import env from '../env';
import { getAuthCookieName, removeAuthCookie } from './authCookie';

const cookies = new Cookies();

function getToken(): string | null {
  return cookies.get(getAuthCookieName());
}

// Clear auth cookie and redirect to logout - used when 401 is received
function logoutUser(): void {
  removeAuthCookie(cookies);
  // Redirect to LOGOUT_PATH to properly sign out via OIDC
  if (window.location.pathname !== LOGOUT_PATH) {
    window.location.replace(LOGOUT_PATH);
  }
}

// Check if a valid token exists (useful for pre-flight checks)
export function hasToken(): boolean {
  return !!getToken();
}

async function fetchWithAuth(
  url: string,
  method: string,
  operationName: string,
  body?: { [key: string]: any },
): Promise<any> {

  const token = getToken();

  if (!token) {
    // If no token is found, redirect to unauthorized page
    window.location.replace(UNAUTHORIZED_PATH);
    throw new Error('Unauthorized: No authentication token found');
  }

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    operationName: operationName,
    'Content-Type': 'application/json',
  };

  const requestOptions: RequestInit = {
    method: method,
    headers: headers,
    body: body ? JSON.stringify(body) : null,
  };

  try {
    // If the URL is relative (doesn't start with http), prepend the base URL
    const fullUrl = url.startsWith('http') ? url : `${env.QAP_API_URL}${url}`;
    const response = await fetch(fullUrl, requestOptions);
    const data = await response.json();

    if (response.status === 403) {
      // Redirect to forbidden page and throw error to stop execution
      window.location.replace(FORBIDDEN_PATH);
      throw new Error('Forbidden: Access denied');
    }

    if (response.status === 401) {
      logoutUser();
      throw new Error('Unauthorized: Authentication required');
    }

    if (!response.ok) {
      const error = new Error(data.message) as Error & { data: any };
      error.data = data;
      throw error;
    }

    return data;
  } catch (error: any) {
    console.error('Fetch Error:', error.message);
    throw error;
  }
}

// GET request
export async function fetchData(url: string, operationName: string): Promise<any> {
  return await fetchWithAuth(url, 'GET', operationName);
}

// PATCH request
export async function updateData(
  url: string,
  operationName: string,
  body: { [key: string]: any },
): Promise<any> {
  return await fetchWithAuth(url, 'PATCH', operationName, body);
}


// PUT request
export async function putData(
  url: string,
  operationName: string,
  body: { [key: string]: any },
): Promise<any> {
  return await fetchWithAuth(url, 'PUT', operationName, body);
}

// POST request
export async function postData(
  url: string,
  operationName: string,
  body: { [key: string]: any },
): Promise<any> {
  return await fetchWithAuth(url, 'POST', operationName, body);
}

// DELETE request
export async function deleteData(
  url: string,
  operationName: string,
): Promise<any> {
  return await fetchWithAuth(url, 'DELETE', operationName);
}

// Download file (returns blob) - supports both GET and POST
export async function downloadFile(
  url: string,
  operationName: string,
  method: 'GET' | 'POST' = 'GET',
  body?: { [key: string]: any },
): Promise<Blob> {
  const token = getToken();

  if (!token) {
    window.location.replace(UNAUTHORIZED_PATH);
    throw new Error('Unauthorized: No authentication token found');
  }

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    operationName: operationName,
  };

  // Only set Content-Type for POST requests with body
  if (method === 'POST' && body) {
    headers['Content-Type'] = 'application/json';
  }

  const requestOptions: RequestInit = {
    method: method,
    headers: headers,
    body: body ? JSON.stringify(body) : null,
  };

  try {
    const fullUrl = url.startsWith('http') ? url : `${env.QAP_API_URL}${url}`;
    const response = await fetch(fullUrl, requestOptions);

    if (response.status === 403) {
      window.location.replace(FORBIDDEN_PATH);
      throw new Error('Forbidden: Access denied');
    }

    if (response.status === 401) {
      logoutUser();
      throw new Error('Unauthorized: Authentication required');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to download file' }));
      throw new Error(errorData.message || 'Failed to download file');
    }

    return await response.blob();
  } catch (error: any) {
    console.error('Download Error:', error.message);
    throw error;
  }
}

/** Parse filename from Content-Disposition header (attachment; filename="..." or filename*=UTF-8''...) */
function parseFilenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;
  const filenameStar = header.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
  if (filenameStar) return decodeURIComponent(filenameStar[1].replace(/"/g, ''));
  const filename = header.match(/filename="?([^";]+)"?/i);
  return filename ? filename[1].trim() : undefined;
}

export interface DownloadWithMetadataResult {
  blob: Blob;
  filename?: string;
  contentType?: string;
}

// Download file and return blob with filename and content-type from response headers
export async function downloadFileWithMetadata(
  url: string,
  operationName: string,
  method: 'GET' | 'POST' = 'GET',
  body?: { [key: string]: any },
): Promise<DownloadWithMetadataResult> {
  const token = getToken();

  if (!token) {
    window.location.replace(UNAUTHORIZED_PATH);
    throw new Error('Unauthorized: No authentication token found');
  }

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    operationName: operationName,
  };

  if (method === 'POST' && body) {
    headers['Content-Type'] = 'application/json';
  }

  const requestOptions: RequestInit = {
    method: method,
    headers: headers,
    body: body ? JSON.stringify(body) : null,
  };

  try {
    const fullUrl = url.startsWith('http') ? url : `${env.QAP_API_URL}${url}`;
    const response = await fetch(fullUrl, requestOptions);

    if (response.status === 403) {
      window.location.replace(FORBIDDEN_PATH);
      throw new Error('Forbidden: Access denied');
    }

    if (response.status === 401) {
      logoutUser();
      throw new Error('Unauthorized: Authentication required');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to download file' }));
      throw new Error(errorData.message || 'Failed to download file');
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const contentType = response.headers.get('Content-Type') ?? undefined;
    const filename = parseFilenameFromContentDisposition(contentDisposition);

    return { blob, filename, contentType };
  } catch (error: any) {
    console.error('Download Error:', error.message);
    throw error;
  }
}

